import { useEffect, useState } from 'react';
import { useJsonLd } from './seo';
import { reviewsAPI, shippingAPI } from './api';

const SITE = 'https://www.otopieces.com';
const abs = (u) => (u && u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const GTIN_KEYS = { 8: 'gtin8', 12: 'gtin12', 13: 'gtin13', 14: 'gtin14' };
const SPEC_SKIP = new Set(['marque', 'reference', 'référence', 'ean', 'prix', 'etat', 'état', 'categorie', 'catégorie', 'sous-categorie', 'sous-catégorie']);

// « Filtre à huile BOSCH F 026 407 088 » — brand/ref appended only when the name doesn't already carry them
export const schemaProductName = (product, name) => {
  const base = (name || product?.name || '').trim();
  const low = base.toLowerCase();
  return [base, ...[product?.brand, product?.ref].filter((x) => x && !low.includes(String(x).toLowerCase()))].join(' ').trim();
};

const gtin = (product) => {
  const digits = String(product?.ean || '').replace(/\D/g, '');
  const key = GTIN_KEYS[digits.length];
  return key ? { [key]: digits } : {};
};

// Technical attributes (height, diameter, type…) → PropertyValue, shown by Google as « Caractéristiques »
export const specProperties = (product, limit = 12) => {
  const seen = new Set();
  return Object.entries(product?.attributes || {})
    .filter(([k, v]) => {
      const base = String(k).replace(/\s*\[[^\]]+\]\s*$/, '').trim().toLowerCase();
      if (!k || v === null || v === '' || SPEC_SKIP.has(String(k).trim().toLowerCase()) || seen.has(base)) return false;
      seen.add(base);
      return true;
    })
    .slice(0, limit)
    .map(([k, v]) => {
      const unit = /\[([^\]]+)\]\s*$/.exec(k);
      return { '@type': 'PropertyValue', name: k.replace(/\s*\[[^\]]+\]\s*$/, '').trim(), value: String(v).trim(), ...(unit ? { unitText: unit[1] } : {}) };
    });
};

export const specSummary = (product, maxLen = 110) => {
  const parts = [];
  for (const p of specProperties(product, 6)) {
    const piece = `${p.name} : ${p.value}${p.unitText ? ` ${p.unitText}` : ''}`;
    if ([...parts, piece].join(' · ').length > maxLen) break;
    parts.push(piece);
  }
  return parts.join(' · ');
};

// Append the spec summary to a meta description when there is room left (Google truncates ~158 chars)
export const enrichDescription = (desc, product, maxLen = 158) => {
  const d = (desc || '').trim();
  const room = maxLen - d.length - 3;
  if (room < 30) return d;
  const s = specSummary(product, room);
  return s ? `${d} · ${s}` : d;
};

let shippingCache = null;
const loadShipping = async () => {
  if (shippingCache) return shippingCache;
  try { shippingCache = await shippingAPI.summary('FR'); } catch { shippingCache = { price: 7.99, free_from: null }; }
  return shippingCache;
};
// Crawler-friendly image URL: /api/files/{id} → /api/files/{id}/{slug}.jpg (trailing filename ignored by the API)
export const seoImageUrl = (u, product) => {
  if (!u) return u;
  const named = /^\/api\/files\/[^/]+$/.test(u) ? `${u}/${String(product?.slug || product?.id || 'image').slice(0, 80)}.jpg` : u;
  return abs(named);
};

const shipping = (price, ship) => ({
  '@type': 'OfferShippingDetails',
  shippingRate: { '@type': 'MonetaryAmount', value: (ship?.free_from != null && price >= ship.free_from) ? '0.00' : Number(ship?.price ?? 7.99).toFixed(2), currency: 'EUR' },
  shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'FR' },
  deliveryTime: {
    '@type': 'ShippingDeliveryTime',
    handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
    transitTime: { '@type': 'QuantitativeValue', minValue: 2, maxValue: 5, unitCode: 'DAY' },
  },
});

const returnPolicy = {
  '@type': 'MerchantReturnPolicy',
  applicableCountry: 'FR',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 14,
  returnMethod: 'https://schema.org/ReturnByMail',
  returnFees: 'https://schema.org/ReturnShippingFees',
};

const priceValidUntil = () => { const d = new Date(); d.setMonth(d.getMonth() + 3); return d.toISOString().slice(0, 10); };

// Google Product rich results: price, availability, brand, sku/mpn, rating + individual reviews, shipping & return policy.
export const useProductJsonLd = (product, { categoryName, canonicalPath } = {}) => {
  const [reviews, setReviews] = useState(null);
  const [ship, setShip] = useState(shippingCache);

  useEffect(() => { loadShipping().then(setShip); }, []);

  useEffect(() => {
    setReviews(null);
    if (!product?.id) return;
    reviewsAPI.getForProduct(product.id).then(r => setReviews(r)).catch(() => setReviews({ reviews: [], count: 0, average: 0 }));
  }, [product?.id]);

  const stock = product ? (product.available_stock ?? product.stock) : 0;
  const url = `${SITE}${canonicalPath || (product ? `/produit/${product.slug || product.id}` : '')}`;
  const count = reviews?.count ?? product?.reviews ?? 0;
  const average = reviews?.average || product?.rating || 0;

  useEffect(() => {
    const tags = product ? {
      'product:price:amount': Number(product.price).toFixed(2),
      'product:price:currency': 'EUR',
      'product:availability': stock > 0 ? 'in stock' : 'out of stock',
      'product:brand': product.brand,
      'product:retailer_item_id': product.ref,
      'product:condition': 'new',
    } : {};
    const created = Object.entries(tags).map(([property, content]) => {
      let el = document.head.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
      return el;
    });
    return () => created.forEach(el => el.remove());
  }, [product, stock]);

  useJsonLd('product', product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${url}#product`,
    name: schemaProductName(product),
    sku: product.ref,
    mpn: product.ref,
    ...gtin(product),
    ...(() => { const props = [...specProperties(product), ...(product.oem_refs || []).slice(0, 5).map(r => ({ '@type': 'PropertyValue', name: 'Référence OEM', value: r }))]; return props.length ? { additionalProperty: props } : {}; })(),
    brand: { '@type': 'Brand', name: product.brand },
    image: (product.images?.length ? product.images : [product.image]).filter(Boolean).map((u) => seoImageUrl(u, product)),
    description: (product.description || '').split(/\n\s*\n/)[0] || `${product.name} ${product.brand} (réf. ${product.ref})${product.type ? ` — ${product.type}` : ''}${categoryName ? `, catégorie ${categoryName}` : ''}.`,
    ...(categoryName ? { category: categoryName } : {}),
    url,
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'EUR',
      price: Number(product.price).toFixed(2),
      priceValidUntil: priceValidUntil(),
      itemCondition: 'https://schema.org/NewCondition',
      availability: stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      ...(stock > 0 ? { inventoryLevel: { '@type': 'QuantitativeValue', value: stock } } : {}),
      seller: { '@type': 'Organization', name: 'OTOPIECES.COM', url: SITE },
      shippingDetails: shipping(Number(product.price), ship),
      hasMerchantReturnPolicy: returnPolicy,
    },
    ...(count > 0 && average > 0 ? {
      aggregateRating: { '@type': 'AggregateRating', ratingValue: Number(average).toFixed(1), bestRating: '5', worstRating: '1', reviewCount: count },
    } : {}),
    ...(reviews?.reviews?.length ? {
      review: reviews.reviews.slice(0, 5).map(r => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.user_name },
        datePublished: (r.created_at || '').slice(0, 10),
        ...(r.title ? { name: r.title } : {}),
        reviewBody: r.comment,
        reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: '5', worstRating: '1' },
        ...(r.admin_reply ? { comment: { '@type': 'Comment', text: r.admin_reply, author: { '@type': 'Organization', name: 'OTOPIECES.COM' } } } : {}),
      })),
    } : {}),
  } : null);

  return { reviewCount: count, average };
};
